import { lazy, Suspense } from "react";

import { Provider } from "react-redux";

import { QueryClientProvider } from "@tanstack/react-query";
import { Router } from "react-router-dom";
import FullStory from "services/Fullstory";
import { ConfirmProvider } from "containers/Confirm/Confirm.context";

import RouteAuthorization from "core/RouteAuthorization";
import RouteDispatcher from "core/RouteDispatcher";

import useVersionCheck from "./hooks/useVersionCheck";
import { ModalContextProvider } from "./contexts/ModalContext";

import appConfig from "./config/appConfig";
import rootSaga from "./sagas/rootSaga";
import store from "./store/configureStore";
import historyInstance from "./utils/history";

import GlobalStyles from "./components/GlobalStyles/GlobalStyles.component";
import NetworkStatus from "./components/NetworkStatus/NetworkStatus.component";

import queryClient from "./services/QueryClient";
import Routes from "./Routes";
import AppErrorBoundary from "./App.ErrorBoundary";
import AppGlobalStyles from "./App.GlobalStyles";
import routesMapped from "./routesMapped";
import routesObject from "./routesObject";
import MainLayout from "./MainLayout";


const ReactQueryDevtools = import.meta.env.DEV ?
  lazy(async() => ({
    default: (await import("@tanstack/react-query-devtools")).ReactQueryDevtools
  })) :
  null;

routesMapped.init(routesObject);
store.runSaga(rootSaga);
appConfig();
FullStory.init();

export default function App() {

  useVersionCheck({ disabled: import.meta.env.DEV });

  return (
    <AppErrorBoundary>
      <AppGlobalStyles />
      <GlobalStyles />
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <Router history={historyInstance}>
            <ConfirmProvider>
              <NetworkStatus />
              <RouteDispatcher>
                <ModalContextProvider>
                  <MainLayout>
                    <RouteAuthorization>
                      <Routes />
                    </RouteAuthorization>
                  </MainLayout>
                </ModalContextProvider>
              </RouteDispatcher>
            </ConfirmProvider>
          </Router>
        </Provider>
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          </Suspense>
        )}
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
